import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { formatDateTR, formatDateTimeTR } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, MagnifyingGlass, Printer, Eye,
  FileText, ClockCounterClockwise, ShieldCheck, Image as ImageIcon, Files,
} from "@phosphor-icons/react";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const STATUS_BADGE = {
  submitted: { label: "Beklemede",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:  { label: "Onaylandı",   cls: "bg-[#F7FBE9] text-[#5c8113] border-[#95C11F]" },
  rejected:  { label: "Reddedildi",  cls: "bg-red-50 text-red-700 border-red-300" },
};

const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                     "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

export default function ExpenseApprovals() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "yonetici";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "submitted", q: "" });

  // Detay + aksiyon dialogları
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status !== "__all__") params.status = filter.status;
      const { data } = await axios.get(`${API}/expense-claims`, { params });
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status]);

  const filtered = useMemo(() => {
    if (!filter.q) return rows;
    const q = filter.q.toLowerCase();
    return rows.filter(r =>
      (r.code || "").toLowerCase().includes(q) ||
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.site_name || "").toLowerCase().includes(q)
    );
  }, [rows, filter.q]);

  const { sort, toggleSort, sortedRows: filteredSorted } = useSortableRows(filtered, {
    defaultKey: "form_date", defaultDir: "desc",
    getters: {
      code: (r) => r.code || "",
      user_name: (r) => r.user_name || "",
      site_name: (r) => r.site_name || "",
      period: (r) => `${r.period_year}-${String(r.period_month || 0).padStart(2, "0")}`,
      form_date: (r) => r.form_date || "",
      expense_count: (r) => Number(r.expense_count) || 0,
      total_amount: (r) => Number(r.total_amount) || 0,
      status: (r) => r.status || "",
    },
  });

  const openDetail = async (r) => {
    setDetailLoading(true);
    setDetail({ ...r, expenses: [] });
    try {
      const { data } = await axios.get(`${API}/expense-claims/id/${r.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatErr(e)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const openPdf = (cid) => window.open(`${API}/expense-claims/id/${cid}/pdf`, "_blank");
  const openPdfWithReceipts = (cid) => window.open(`${API}/expense-claims/id/${cid}/pdf-with-receipts`, "_blank");

  const approve = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await axios.post(`${API}/expense-claims/id/${detail.id}/approve`);
      toast.success(`${detail.code} onaylandı — tüm masraflar kilitlendi`);
      setApproveDialog(false); setDetail(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setActing(false); }
  };

  const reject = async () => {
    if (!detail) return;
    if (!rejectReason.trim()) { toast.error("Red gerekçesi girin"); return; }
    setActing(true);
    try {
      await axios.post(`${API}/expense-claims/id/${detail.id}/reject`, { reason: rejectReason });
      toast.success(`${detail.code} reddedildi — masraflar serbest bırakıldı`);
      setRejectDialog(false); setRejectReason(""); setDetail(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setActing(false); }
  };

  const totalWaiting = rows.filter(r => r.status === "submitted").length;
  const totalAmount = filtered.reduce((s, r) => s + (r.total_amount || 0), 0);

  if (!isAdmin) {
    return (
      <Shell title="Masraf Onayları" subtitle="Masraf Bildirimi" hideSiteSelector>
        <Card className="p-8 rounded-none sm:rounded-sm text-center">
          <ShieldCheck size={48} weight="duotone" className="mx-auto text-slate-300 mb-3" />
          <div className="text-lg font-bold text-slate-700">Yetkisiz Erişim</div>
          <div className="text-sm text-slate-500 mt-1">Bu ekran yalnızca onay yetkisi olan kullanıcılar içindir.</div>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell
      title="Masraf Onayları"
      subtitle="Masraf Bildirimi › Onay Bekleyen Formlar"
      hideSiteSelector
    >
      {/* Özet kartlar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-amber-500 bg-amber-50">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Onay Bekliyor</div>
          <div className="text-2xl font-mono font-bold text-amber-900" data-testid="approvals-waiting-count">{totalWaiting}</div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#1B4776] bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Filtrelenen</div>
          <div className="text-2xl font-mono font-bold text-[#1B4776]" data-testid="approvals-count">{filtered.length} form</div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#95C11F] bg-[#F7FBE9] md:col-span-1 col-span-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5c8113]">Toplam Bedel</div>
          <div className="text-2xl font-mono font-bold text-[#5c8113]" data-testid="approvals-total">
            ₺{totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
        </Card>
      </div>

      {/* Filtre */}
      <Card className="p-3 rounded-none sm:rounded-sm mb-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ara</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <Input value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })}
                   data-testid="approvals-filter-q" placeholder="Form no / personel / şantiye…"
                   className="pl-7 rounded-none sm:rounded-sm h-9" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</Label>
          <Select value={filter.status} onValueChange={v => setFilter({ ...filter, status: v })}>
            <SelectTrigger data-testid="approvals-filter-status" className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="submitted">Onay Bekleyenler</SelectItem>
              <SelectItem value="approved">Onaylananlar</SelectItem>
              <SelectItem value="rejected">Reddedilenler</SelectItem>
              <SelectItem value="__all__">Tümü (taslak dahil)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <ShieldCheck size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">
            {filter.status === "submitted" ? "Onay bekleyen form yok" : "Bu filtreye uyan form bulunamadı"}
          </div>
          <div className="text-xs text-slate-500">
            {filter.status === "submitted"
              ? "Personel talep formu gönderdiğinde burada listelenecek."
              : "Farklı bir durum filtresi seçin."}
          </div>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
                <tr>
                  <SortableTh label="Form No" k="code" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                  <SortableTh label="Personel" k="user_name" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                  <SortableTh label="Proje / Birim" k="site_name" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                  <SortableTh label="Dönem" k="period" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                  <SortableTh label="Form Tarihi" k="form_date" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                  <SortableTh label="Kalem" k="expense_count" sort={sort} onSort={toggleSort} className="text-center px-3 py-2" align="center" />
                  <SortableTh label="Toplam" k="total_amount" sort={sort} onSort={toggleSort} className="text-right px-3 py-2" align="right" />
                  <SortableTh label="Durum" k="status" sort={sort} onSort={toggleSort} className="text-center px-3 py-2" align="center" />
                  <th className="text-right px-3 py-2 pr-4">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map((r, i) => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.submitted;
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`approval-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs font-bold text-[#1B4776]">{r.code}</td>
                      <td className="px-3 py-2 text-xs">{r.user_name}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.site_name || <span className="text-slate-400">— Genel —</span>}
                        {r.site_code && <span className="text-[10px] text-slate-400 font-mono ml-1">({r.site_code})</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {MONTH_NAMES[(r.period_month || 1) - 1]} {r.period_year}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{formatDateTR(r.form_date)}</td>
                      <td className="px-3 py-2 text-center text-xs">{r.expense_count}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-[#1B4776]">
                        ₺{(r.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 pr-4 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`approval-detail-${i}`}
                                  className="h-7 px-2" title="Detayı Gör">
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openPdf(r.id)} data-testid={`approval-pdf-${i}`}
                                  className="h-7 px-2" title="PDF">
                            <Printer size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detay + Aksiyon Dialogu */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none sm:rounded-sm">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <FileText size={20} weight="duotone" className="text-[#1B4776]" />
                  <span>Masraf Talep Formu — {detail.code}</span>
                  <span className={`ml-auto px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${STATUS_BADGE[detail.status]?.cls}`}>
                    {STATUS_BADGE[detail.status]?.label}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {detail.user_name} · {detail.site_name || "— Genel —"} · {formatDateTR(detail.form_date)}
                </DialogDescription>
              </DialogHeader>

              {detailLoading ? (
                <div className="py-8 text-center text-sm text-slate-500">Yükleniyor…</div>
              ) : (
                <>
                  {/* Üst bilgi */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">Görevi</div>
                      <div className="font-medium text-slate-800">{detail.user_position || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">Dönem</div>
                      <div className="font-medium text-slate-800">
                        {MONTH_NAMES[(detail.period_month || 1) - 1]} {detail.period_year}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">IBAN</div>
                      <div className="font-mono text-slate-800 truncate" title={detail.user_iban}>
                        {detail.user_iban ? detail.user_iban.match(/.{1,4}/g).join(" ") : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">Toplam</div>
                      <div className="text-lg font-mono font-bold text-[#95C11F]">
                        ₺{(detail.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  {/* Kalemler */}
                  <div className="border border-slate-200 mt-2 overflow-hidden">
                    <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-600 border-b border-slate-200">
                      Masraf Kalemleri ({detail.expense_count || 0})
                    </div>
                    <div className="overflow-x-auto max-h-[40vh]">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-[9px] uppercase tracking-widest font-bold text-slate-600 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5">Tarih</th>
                            <th className="text-left px-2 py-1.5">Kategori</th>
                            <th className="text-left px-2 py-1.5">Firma / Açıklama</th>
                            <th className="text-right px-2 py-1.5">Brüt</th>
                            <th className="text-right px-2 py-1.5">KDV</th>
                            <th className="text-right px-2 py-1.5">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.expenses || []).map((e, i) => (
                            <tr key={e.id} className="border-t border-slate-100" data-testid={`approval-detail-item-${i}`}>
                              <td className="px-2 py-1 font-mono">{formatDateTR(e.date)}</td>
                              <td className="px-2 py-1">{e.category_label}</td>
                              <td className="px-2 py-1">
                                {e.vendor_name && <div className="font-medium">{e.vendor_name}</div>}
                                <div className="text-slate-500">{e.description || "-"}</div>
                                {e.vendor_tax_number && <div className="text-[9px] font-mono text-slate-400">VKN {e.vendor_tax_number}</div>}
                              </td>
                              <td className="px-2 py-1 text-right font-mono font-bold text-[#1B4776]">
                                ₺{(e.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-amber-700">
                                ₺{(e.vat_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                ₺{(e.net_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                            <td colSpan={3} className="px-2 py-1.5 text-right text-[9px] uppercase tracking-widest">Toplam</td>
                            <td className="px-2 py-1.5 text-right font-mono text-[#1B4776]">
                              ₺{(detail.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-amber-700">
                              ₺{(detail.vat_total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              ₺{(detail.net_total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Notlar ve önceki karar bilgileri */}
                  {(() => {
                    const withReceipts = (detail.expenses || []).filter(e => e.invoice_file_path);
                    if (withReceipts.length === 0) return null;
                    return (
                      <div className="mt-2 p-2 bg-slate-50 border border-slate-200">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1.5 flex items-center gap-1.5">
                          <ImageIcon size={12} weight="duotone" className="text-[#1B4776]" />
                          Yüklü Fişler ({withReceipts.length} / {detail.expense_count || 0})
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                          {withReceipts.map((e, i) => (
                            <a
                              key={e.id}
                              href={`${API}/files/${e.invoice_file_path}`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`approval-receipt-link-${i}`}
                              className="flex items-center gap-1.5 px-2 py-1 text-[11px] bg-white border border-slate-200 hover:border-[#1B4776] hover:bg-blue-50 truncate"
                              title={`${e.vendor_name || e.description || ''} · ₺${(e.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`}
                            >
                              <ImageIcon size={11} className="text-slate-500 shrink-0" />
                              <span className="truncate">
                                <b>#{i + 1}</b> {e.vendor_name || e.description || `${e.category_label}`}
                              </span>
                              <span className="text-slate-400 ml-auto font-mono text-[10px]">
                                ₺{(e.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0 })}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {detail.notes && (
                    <div className="mt-2 p-2 bg-slate-50 border-l-4 border-slate-300 text-xs">
                      <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Notlar</div>
                      <div className="text-slate-700 mt-0.5">{detail.notes}</div>
                    </div>
                  )}

                  {detail.status === "rejected" && detail.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-400 text-xs">
                      <div className="text-[9px] uppercase tracking-widest font-bold text-red-700">Red Gerekçesi</div>
                      <div className="text-red-800 mt-0.5">{detail.rejection_reason}</div>
                      {detail.approved_by_name && (
                        <div className="text-[10px] text-red-600 mt-1 flex items-center gap-1">
                          <ClockCounterClockwise size={11} />
                          Reddeden: <b>{detail.approved_by_name}</b>
                          {detail.approved_at && <span> · {formatDateTimeTR(detail.approved_at)}</span>}
                        </div>
                      )}
                    </div>
                  )}
                  {detail.status === "approved" && detail.approved_by_name && (
                    <div className="mt-2 p-2 bg-[#F7FBE9] border-l-4 border-[#95C11F] text-xs">
                      <div className="text-[9px] uppercase tracking-widest font-bold text-[#5c8113]">Onay Bilgisi</div>
                      <div className="text-[#5c8113] mt-0.5 flex items-center gap-1">
                        <ClockCounterClockwise size={11} />
                        Onaylayan: <b>{detail.approved_by_name}</b>
                        {detail.approved_at && <span> · {formatDateTimeTR(detail.approved_at)}</span>}
                      </div>
                    </div>
                  )}
                </>
              )}

              <DialogFooter className="border-t pt-3 mt-2 flex flex-wrap gap-2 justify-between sm:justify-end">
                <div className="flex gap-2 flex-1 sm:flex-none flex-wrap">
                  <Button variant="outline" onClick={() => openPdf(detail.id)}
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
                    <Printer size={14} className="mr-1.5" /> PDF
                  </Button>
                  <Button variant="outline" onClick={() => openPdfWithReceipts(detail.id)}
                          data-testid="approval-pdf-with-receipts"
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#F7FBE9] border-[#95C11F] text-[#5c8113] hover:bg-[#eef7d0]">
                    <Files size={14} className="mr-1.5" /> Fişlerle PDF
                  </Button>
                  <Button variant="outline" onClick={() => nav(`/masraf/talep/${detail.id}`)}
                          data-testid="approval-open-detail-page"
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
                    Detay Sayfası
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDetail(null)}
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
                    Kapat
                  </Button>
                  {detail.status === "submitted" && (
                    <>
                      <Button onClick={() => setRejectDialog(true)} data-testid="approval-reject-btn"
                              className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white">
                        <XCircle size={14} className="mr-1.5" /> Reddet
                      </Button>
                      <Button onClick={() => setApproveDialog(true)} data-testid="approval-approve-btn"
                              className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7fa519] text-white">
                        <CheckCircle size={14} className="mr-1.5" /> Onayla
                      </Button>
                    </>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Onay Onay Dialog */}
      <Dialog open={approveDialog} onOpenChange={o => !o && setApproveDialog(false)}>
        <DialogContent className="max-w-md rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>Formu Onayla</DialogTitle>
            <DialogDescription className="text-xs">
              {detail?.code} formunu onaylıyorsunuz. İçerideki <b>{detail?.expense_count || 0} masraf</b> da
              otomatik olarak onaylanacak ve <b>kilitlenecek</b>. Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-[#F7FBE9] border-l-4 border-[#95C11F] text-xs">
            <div className="flex justify-between">
              <span className="text-slate-600">Toplam Ödeme:</span>
              <b className="font-mono text-[#5c8113]">
                ₺{(detail?.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
              </b>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-slate-600">Personel:</span>
              <b>{detail?.user_name}</b>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-slate-600">IBAN:</span>
              <b className="font-mono">{detail?.user_iban || "—"}</b>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
            <Button onClick={approve} disabled={acting} data-testid="approval-approve-confirm"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7fa519] text-white">
              {acting ? "…" : "Onayla ve Kilitle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={o => !o && setRejectDialog(false)}>
        <DialogContent className="max-w-md rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>Formu Reddet</DialogTitle>
            <DialogDescription className="text-xs">
              Personel <b>{detail?.user_name}</b> göreceği red gerekçesini yazın.
              Reddettiğinizde formdaki masraflar serbest bırakılır, personel düzeltip yeniden gönderebilir.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4}
                    placeholder="Örn: Fatura fotoğrafı okunmuyor, yeniden yükleyin."
                    data-testid="approval-reject-reason"
                    className="rounded-none sm:rounded-sm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialog(false); setRejectReason(""); }}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
            <Button onClick={reject} disabled={acting} data-testid="approval-reject-confirm"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white">
              {acting ? "…" : "Reddet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
